<script>
	import { crossfade } from 'svelte/transition';
	import {animatePages, customCss, pages, useCustomCss} from '../share/stores';
	import { cssAnimationDuration } from '../share/utils';
	import { calculatePageAnimation } from '../share/pages';
	
	import ClosePageButton from '../components/ClosePageButton.svelte';
	import FetchPage from '../fetch/FetchPage.svelte';

	export let closeButtonClasses;
	export let closeButtonText;

	export let usePages = [];
	export let name = '';
	export let showCloseButton = false;

	export let isRightSide;
	export let rightSideRendered;

	const animatedPagesDoneEvent = new Event('animatedPagesDone');
	
	// This function works different when moved in a .js file
	const [send, receive] = crossfade({
		duration: d => $animatePages ? cssAnimationDuration() : 0,
		
		fallback(node, params) {
			const animationValues = calculatePageAnimation(name, params.out);
			
			let rightStart = animationValues.rightStart;
			let rightEnd = animationValues.rightEnd;
			
			return {
				duration: $animatePages ? cssAnimationDuration() : 0,
				
				css: (transition) => {
					const parentContainer = node.parentElement;
					if(parentContainer && node.classList.contains('now') && !params.out) {
						parentContainer.scrollTop = 0;
					}
					const inOutTransition = params.out ? 1 - transition : transition;
					const calculatedRight = inOutTransition * (rightEnd - rightStart) + rightStart;
					if(calculatedRight === 0) {
						window.dispatchEvent(animatedPagesDoneEvent);
					}
					return `
						transform: translateX(${-calculatedRight}%);
					`;
				}
			};
		}
	});
</script>

<!-- MOST IMPORTANT CHANGE - use: -->
<!-- ... as page (page.id)} -->
<!-- NOT JUST: -->
<!-- ... as page} -->

<!-- we could also use the given CSS to show a scrollbar like: -->
<!--	scrollbar-width: thin; -->
<!--	scrollbar-color: rgba(111, 123, 138, 0.4) #ffffff; -->

{#each usePages as page (page.id)}
	<div
		class="outer {name}"
		in:receive="{{key: page.id}}"
		out:send="{{key: page.id, out: true}}"
		style={$useCustomCss && isRightSide? $customCss : ''}
		class:full-width={$pages.length <= 2}
	>
		<div class="inner">
			{#if showCloseButton}
				<ClosePageButton extraClass={closeButtonClasses} text={closeButtonText}/>
			{/if}
			<FetchPage {isRightSide} bind:rightSideRendered route={page.path} bind:customCss={page.css}/>
		</div>
	</div>
{/each}

<style lang="scss">
	@import "../style/variables";
	@import "../style/mixins";
	
	.outer {
		position: absolute;
		width: 100%;
		top: 0;
		
		&.prev {
			width: 90vw;
			right: 0;

			&.full-width {
				width: 100vw;
			}
			
			&:first-of-type {
				z-index: 100;
			}

			:global(.inner > ul) {
				margin-top: -5.5rem;

				@include for-desktop-up {
					margin-top: -8rem;
				}
			}
		}
		
		&.now {
			display: grid;
			grid-template-columns: minmax(auto, calc(var(--max-main-width))) 0 0;
			height: 100%; /* makes footer stick to bottom on small pages: */
			
			> .inner {
				background-color: inherit;
				
				/* to make footer stick to bottom on small pages: */
				height: max-content;
				min-height: 100%;
				display: flex;
				flex-direction: column;
			}
		}
	}
	
	:global(.multiplePages) .now {
		grid-template-columns: minmax(auto, calc(var(--max-main-width) - 10vw)) 0 0;
	}
	
	@media screen and (min-width: #{$max-main-width}) {
		.now {
			grid-template-columns: minmax(auto, calc(var(--max-main-width))) 0 auto;
			
			width: 100%;
			margin: auto;
			position: relative;
		}
		
		:global(.multiplePages) .now {
			grid-template-columns: minmax(auto, calc(var(--max-main-width) - 5vw)) 5vw auto;
			right: 0;
			
			/* Hide scrollbar in Non-WebKit-Browsers */
			-ms-overflow-style: none;
			scrollbar-width: none;
			
			/* Hide scrollbar in WebKit-Browsers (Chrome, Safari and Opera) */
			&::-webkit-scrollbar {
				display: none;
			}
		}
	}
	
	$max-main-width-now: calc($max-main-width / $top-navigation-width-divisor);

	.inner {

		:global(> ul > li > div:not(.no_margin):not(:last-of-type)) {
			margin-bottom: $content-divs-spacing !important;
			margin-top: 0 !important;
			width: 100%;
		}

		:global(.marginbox) {
			width: calc(100% - px-to-em(20) - px-to-em(20));
			margin-left: px-to-em(20);
			margin-right: px-to-em(20);

			@include for-tablet-landscape-up {
				width: calc(100% - px-to-rem(142) - px-to-rem(142));
				margin-left: px-to-rem(142);
				margin-right: px-to-rem(142);
			}
		}
		:global(.marginbox_left) {
			width: calc(100% - px-to-em(20));
			margin-left: px-to-em(20);

			@include for-tablet-landscape-up {
				width: calc(100% - px-to-rem(142));
				margin-left: px-to-rem(142);
			}
		}
		:global(.marginbox_right) {
			width: calc(100% - px-to-em(20));
			margin-right: px-to-em(20);

			@include for-tablet-landscape-up {
				width: calc(100% - px-to-rem(142));
				margin-right: px-to-rem(142);
			}
		}
		:global(.paddingbox) {
			padding-left: px-to-em(20);
			padding-right: px-to-em(20);

			@include for-tablet-landscape-up {
				padding-left: px-to-rem(142);
				padding-right: px-to-rem(142);
			}
		}
		:global(.paddingbox_left) {
			padding-left: px-to-em(20);

			@include for-tablet-landscape-up {
				padding-left: px-to-rem(142);
			}
		}
		:global(.paddingbox_right) {
			padding-right: px-to-em(20);

			@include for-tablet-landscape-up {
				padding-right: px-to-rem(142);
			}
		}
	}

	:global(.now > .inner) {
		position: absolute;
		width: 100%;
	}

	:global(.multiplePages + .content .outer.now > .inner > ul) {
		max-width: 1920px;
	}
</style>
